import type {
  HttpTransport,
  TransportRequestConfig,
} from '@mangovoice/api-client'
import type { Fax, PaginatedApiResult } from '@mangovoice/types'
import type { components } from '@mangovoice/types/generated/mango-api'

import { stringifyParams } from './utils'

export type FaxInstance = components['schemas']['Fax']

export interface GetFaxListParams {
  pbxId: number
  offset?: number
  search?: string
  direction?: 'inbound' | 'outbound'
  extension_id?: number
  unread?: string
  limit?: number
  ordering?: string
}

export interface FaxExtensionCounts {
  extension_id: number
  unread_count: number
  total_count: number
}

export interface FaxUnreadCountsResult {
  // Optional so the type matches the client's defensive `counts ?? []`: a
  // malformed/empty body must not be treated as guaranteed to carry the array.
  counts?: FaxExtensionCounts[]
}

export function createFaxesResource(transport: HttpTransport) {
  return {
    getFaxList: (params: GetFaxListParams, config?: TransportRequestConfig) => {
      const qs = stringifyParams({
        ordering: params.ordering ?? '-id',
        pbx_id: params.pbxId,
        offset: params.offset,
        search: params.search,
        extension_id: params.extension_id,
        unread: params.unread,
        limit: params.limit ?? 50,
        direction: params.direction,
      })
      return transport.get<PaginatedApiResult<Fax>>(
        `/faxes/?${qs}`,
        undefined,
        config,
      )
    },

    getFax: (faxId: number, config?: TransportRequestConfig) =>
      transport.get<FaxInstance>(`/faxes/${faxId}/`, undefined, config),

    // Unread (and total) fax counts grouped by extension for a PBX, in one
    // request, so the nav badge doesn't fan out to one list call per extension.
    getUnreadCounts: (pbxId: number, config?: TransportRequestConfig) => {
      const qs = stringifyParams({ pbx_id: pbxId })
      return transport.get<FaxUnreadCountsResult>(
        `/faxes/unread-counts/?${qs}`,
        undefined,
        config,
      )
    },

    sendFax: (formData: FormData, config?: TransportRequestConfig) =>
      transport.post<unknown>('/faxes/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60 * 1000,
        ...config,
      }),

    resendFax: (faxId: number) =>
      transport.put<unknown>(`/faxes/${faxId}/resend/`),

    deleteFax: (faxId: number) => transport.delete<void>(`/faxes/${faxId}/`),

    moveFaxToVirtualFax: (faxId: number, extensionId: number) =>
      transport.put<{ extension_id: number }>(
        `/faxes/${faxId}/move-to-virtual-fax/`,
        { extension_id: extensionId },
      ),

    markFaxAsRead: (faxId: number) =>
      transport.put<{ message: string; read_at: string }>(
        `/faxes/${faxId}/mark-as-read/`,
      ),

    markFaxAsUnread: (faxId: number) =>
      transport.put<{ message: string }>(`/faxes/${faxId}/mark-as-unread/`),
  }
}
